import { getSchoolsRequest } from '../../../../api/requests/schools';
import { actionKitImpFunction } from '../../../../components/ozmoit/common';
import {
  createCourseGroupRequest,
  deleteCourseGroupRequest,
  getCourseGroupDetailRequest,
  getCourseGroupParentsRequest,
  getCourseGroupsListRequest,
  getCoursesFormDataRequest,
  searchCourseGroupChildrenRequest,
  setCourseGroupItemsRequest,
  updateCourseGroupRequest,
} from '../api';
import {
  ChildrenSearchQuery,
  CourseGroupListQuery,
  CreateCourseGroupBody,
  SetItemsBody,
  UpdateCourseGroupBody,
} from '../types';

// HU 15564 — los wrappers re-lanzan `response.error` (no `new Error(...)`) para
// preservar el shape `{ code, status, data, error }` que produce request.ts
// ante un HTTP 4xx/5xx. Esto permite que los consumidores extraigan el código
// de error de dominio del backend (p. ej. 'COURSE_GROUP_IN_USE') desde
// `error.data.data.code`. Ver utils/extractBackendErrorCode.

const getCourseGroupsListAction = actionKitImpFunction(
  async (params: CourseGroupListQuery) => {
    const response = await getCourseGroupsListRequest(params);
    if (response.error) {
      throw response.error;
    }
    return response.data;
  },
);

const getCourseGroupDetailAction = actionKitImpFunction(
  async (id: number | string) => {
    const response = await getCourseGroupDetailRequest(id);
    if (response.error) {
      throw response.error;
    }
    return response.data;
  },
);

const createCourseGroupAction = actionKitImpFunction(
  async (data: CreateCourseGroupBody) => {
    const response = await createCourseGroupRequest(data);
    if (response.error) {
      throw response.error;
    }
    return response.data;
  },
);

const updateCourseGroupAction = actionKitImpFunction(
  async (id: number | string, data: UpdateCourseGroupBody) => {
    const response = await updateCourseGroupRequest(id, data);
    if (response.error) {
      throw response.error;
    }
    return response.data;
  },
);

const deleteCourseGroupAction = actionKitImpFunction(
  async (id: number | string) => {
    const response = await deleteCourseGroupRequest(id);
    if (response.error) {
      throw response.error;
    }
    return response.data;
  },
);

const getCourseGroupParentsAction = actionKitImpFunction(
  async (id: number | string) => {
    const response = await getCourseGroupParentsRequest(id);
    if (response.error) {
      throw response.error;
    }
    return response.data;
  },
);

const setCourseGroupItemsAction = actionKitImpFunction(
  async (id: number | string, data: SetItemsBody) => {
    const response = await setCourseGroupItemsRequest(id, data);
    if (response.error) {
      throw response.error;
    }
    return response.data;
  },
);

const searchCourseGroupChildrenAction = actionKitImpFunction(
  async (id: number | string, params: ChildrenSearchQuery) => {
    const response = await searchCourseGroupChildrenRequest(id, params);
    if (response.error) {
      throw response.error;
    }
    return response.data;
  },
);

// HU 15564 — Task FE-B: action local para poblar el filtro de escuelas en el
// listado de Agrupaciones. Usa el endpoint existente /schools paginado y pide
// hasta 200 elementos en la primera página, alineado con el patrón usado por
// otros maintainers que cargan todas las escuelas para un select.
const getSchoolsListAction = actionKitImpFunction(async () => {
  const response = await getSchoolsRequest(200, 0);
  if (response.error) {
    throw response.error;
  }
  return response.data;
});

// HU 15571 — Action local para poblar el filtro PLAN DE ESTUDIO del buscador
// de items. Reusa el endpoint /curricular-subjects/form-data del módulo de
// Asignaturas y expone únicamente la lista `studyPlanVersions` (mallas).
const getStudyPlanVersionsListAction = actionKitImpFunction(async () => {
  const response = await getCoursesFormDataRequest();
  if (response.error) {
    throw response.error;
  }
  return response.data;
});

export {
  createCourseGroupAction,
  deleteCourseGroupAction,
  getCourseGroupDetailAction,
  getCourseGroupParentsAction,
  getCourseGroupsListAction,
  getSchoolsListAction,
  getStudyPlanVersionsListAction,
  searchCourseGroupChildrenAction,
  setCourseGroupItemsAction,
  updateCourseGroupAction,
};
